// Pricing helpers for the Formula Calculator.
// All amounts are retail dollars. Labor is a percentage of the component
// subtotal (vase base + vase setup + flower stems) and always applies.

export const LABOR_RATES = [0.225, 0.2];
export const DEFAULT_LABOR_RATE = 0.225;

// Component subtotal for a single arrangement.
export function arrangementSubtotal(vase, flowerCounts, flowers) {
  const vaseCost = vase
    ? (Number(vase.basePrice) || 0) + (Number(vase.setupPrice) || 0)
    : 0;
  const flowerCost = flowers.reduce(
    (sum, f) => sum + (Number(f.pricePerStem) || 0) * (flowerCounts[f.id] || 0),
    0
  );
  return vaseCost + flowerCost;
}

export function laborAmount(subtotal, laborRate) {
  return subtotal * laborRate;
}

// Total for a single arrangement (subtotal + labor).
export function arrangementTotal(subtotal, laborRate) {
  return subtotal * (1 + laborRate);
}

export function totalStemCount(flowerCounts) {
  return Object.values(flowerCounts).reduce((sum, n) => sum + (n || 0), 0);
}

export function formatCurrency(amount) {
  const n = Number(amount) || 0;
  return `$${n.toFixed(2)}`;
}

export const DEFAULT_FLOWER_MARKUP = 3.5;

// Retail per stem derived from a live wholesale cost. markup falsy/<=0 → default.
export function effectiveRetail(wholesale, markup) {
  const w = Number(wholesale) || 0;
  const m = Number(markup) > 0 ? Number(markup) : DEFAULT_FLOWER_MARKUP;
  return w * m;
}

// Per-arrangement margin over the VENDOR-PRICED flowers only (the ones with a
// known real wholesale). Returns null when none are vendor-priced.
export function vendorMargin(flowers, flowerCounts) {
  const priced = flowers.filter((f) => Number(f.vendorCost) > 0);
  if (!priced.length) return null;
  const wholesale = priced.reduce((s, f) => s + Number(f.vendorCost) * (flowerCounts[f.id] || 0), 0);
  const retail = priced.reduce((s, f) => s + (Number(f.pricePerStem) || 0) * (flowerCounts[f.id] || 0), 0);
  const marginPct = retail > 0 ? ((retail - wholesale) / retail) * 100 : 0;
  return { wholesale, retail, marginPct };
}

// Flower-stem breakdown for the order's special instructions. Vase, setup,
// greenery and labor are deliberately excluded — the sheet lists stems only.
export function buildBreakdownText(flowers, flowerCounts, quantity) {
  const lines = flowers
    .filter((f) => (flowerCounts[f.id] || 0) > 0)
    .map((f) => {
      const per = flowerCounts[f.id];
      if (quantity > 1) {
        return `${f.name} — ${per}/${per * quantity}`;
      }
      return { name: f.name, per };
    });

  if (lines.length === 0) return '';

  if (quantity > 1) {
    return `Formula (${quantity} arrangements):\n${lines.join('\n')}`;
  }
  return `Formula: ${lines.map((l) => `${l.per} ${l.name}`).join(', ')}`;
}
